import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Radar, Plus, RefreshCw, Trash2, Edit3, Sparkles, Play, AlertCircle,
  CheckCircle2, Clock, ExternalLink, Image as ImageIcon, Video, Loader2,
  Target, Calendar, X, Eye, Tag, TrendingUp, Building2, Filter,
  Search, Bookmark, ChevronDown, Settings2, CheckSquare, Square,
  Zap, Database, Cpu, Globe, Shield, Network, Brain, Info, Layers, Package, Rocket, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AdDetailModal from '@/components/AdDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQ_LABELS = { off: 'Manual only', daily: 'Daily · 2 AM IST', weekly: 'Weekly · Mon 2 AM IST' };

const StatusDot = ({ status }) => {
  const map = {
    success: 'bg-emerald-500',
    running: 'bg-amber-500 animate-pulse',
    error: 'bg-rose-500',
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${map[status] || 'bg-slate-300'}`} />;
};

const EmptyState = ({ onAdd }) => (
  <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#2EB4AC]/15 to-cyan-100 mb-4">
      <Radar className="h-7 w-7 text-[#2EB4AC]" />
    </div>
    <h3 className="text-lg font-semibold text-slate-900 mb-1">Start tracking a competitor</h3>
    <p className="text-sm text-slate-500 max-w-md mx-auto mb-5">
      Add a brand name, Meta Page ID, or Ad Library URL — PRISM pulls their active ads and lets you analyze them with GPT-4o on demand.
    </p>
    <Button onClick={onAdd} className="bg-[#2EB4AC] hover:bg-[#259993] text-white" data-testid="empty-add-competitor-btn">
      <Plus className="h-4 w-4 mr-2" /> Add your first competitor
    </Button>
  </div>
);

const CompetitorForm = ({ initial, onSave, onClose }) => {
  const [f, setF] = useState({
    brand_name: initial?.brand_name || '',
    search_keyword: initial?.search_keyword || '',
    page_id: initial?.page_id || '',
    page_url: initial?.page_url || '',
    country: initial?.country || 'IN',
    media_type: initial?.media_type || 'image',
    schedule_frequency: initial?.schedule_frequency || 'off',
    schedule_time: initial?.schedule_time || '02:00',
  });
  const [saving, setSaving] = useState(false);

  const canSave = !!(f.brand_name || '').trim();

  // Detect single-ad URL like https://www.facebook.com/ads/library/?id=1006100558099670
  const singleAdId = (() => {
    const u = (f.page_url || '').trim();
    if (!u.includes('/ads/library')) return null;
    const m = u.match(/[?&]id=(\d{6,})/);
    return m ? m[1] : null;
  })();

  const scheduleLabel = {
    off: 'Off (manual only)',
    daily: `Daily at ${f.schedule_time} IST`,
    weekly: `Weekly · Mon at ${f.schedule_time} IST`,
    biweekly: `Bi-weekly · alternate Mon at ${f.schedule_time} IST`,
    monthly: `Monthly · 1st at ${f.schedule_time} IST`,
  }[f.schedule_frequency];

  const handleSubmit = async () => {
    if (!canSave) return;
    setSaving(true);
    await onSave(f);
    setSaving(false);
  };

  return (
    <>
      <div className="space-y-4 py-2">
        <div>
          <Label className="text-xs font-medium text-slate-600 flex items-center gap-1">
            Brand name <span className="text-rose-500">*</span>
          </Label>
          <Input
            value={f.brand_name}
            onChange={(e) => setF(p => ({ ...p, brand_name: e.target.value }))}
            placeholder="e.g. MyProtein, HealthifyMe"
            className="mt-1"
            data-testid="competitor-brand-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-slate-600">Search keyword <span className="text-slate-400">(optional)</span></Label>
            <Input
              value={f.search_keyword}
              onChange={(e) => setF(p => ({ ...p, search_keyword: e.target.value }))}
              placeholder="defaults to brand name"
              className="mt-1"
              data-testid="competitor-keyword-input"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600">Meta Page ID <span className="text-slate-400">(optional)</span></Label>
            <Input
              value={f.page_id}
              onChange={(e) => setF(p => ({ ...p, page_id: e.target.value }))}
              placeholder="e.g. 100063876543210"
              className="mt-1"
              data-testid="competitor-pageid-input"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-medium text-slate-600">Ad Library URL or Page URL <span className="text-slate-400">(optional)</span></Label>
          <Input
            value={f.page_url}
            onChange={(e) => setF(p => ({ ...p, page_url: e.target.value }))}
            placeholder="Paste page URL or specific ad URL (?id=...)"
            className="mt-1"
            data-testid="competitor-url-input"
          />
          {singleAdId ? (
            <p className="text-[11px] text-emerald-600 mt-1 inline-flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Single-ad URL detected — we'll fetch only ad <span className="font-mono font-semibold">{singleAdId}</span>
            </p>
          ) : (
            <p className="text-[11px] text-slate-400 mt-1">
              Provide any one — Page ID is most precise, keyword is broadest. Single-ad URLs (with <span className="font-mono">?id=</span>) are auto-detected.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-slate-600">Media type</Label>
            <Select value={f.media_type} onValueChange={(v) => setF(p => ({ ...p, media_type: v }))}>
              <SelectTrigger className="mt-1" data-testid="competitor-media-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="image">Static (images only)</SelectItem>
                <SelectItem value="video">Video only</SelectItem>
                <SelectItem value="all">All types</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600">Country</Label>
            <div className="mt-1 h-9 px-3 rounded-md border bg-slate-50 flex items-center text-sm text-slate-700">IN</div>
          </div>
        </div>

        {/* Auto-pull schedule */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
          <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Auto-pull schedule</Label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Select value={f.schedule_frequency} onValueChange={(v) => setF(p => ({ ...p, schedule_frequency: v }))}>
              <SelectTrigger data-testid="competitor-freq-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Off (manual only)</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly · Monday</SelectItem>
                <SelectItem value="biweekly">Bi-weekly · alternate Mondays</SelectItem>
                <SelectItem value="monthly">Monthly · 1st of every month</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="time"
              value={f.schedule_time}
              onChange={(e) => setF(p => ({ ...p, schedule_time: e.target.value }))}
              disabled={f.schedule_frequency === 'off'}
              className="disabled:opacity-50"
              data-testid="competitor-time-input"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-2">{scheduleLabel}</p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!canSave || saving} className="bg-[#2EB4AC] hover:bg-[#259993] text-white" data-testid="competitor-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          Save competitor
        </Button>
      </DialogFooter>
    </>
  );
};

const WatchlistRow = ({ item, onPull, onEdit, onDelete, isPulling }) => {
  const last = item.last_pulled_at ? format(new Date(item.last_pulled_at), 'MMM d, HH:mm') : '—';
  return (
    <div className="group flex items-center gap-3 px-4 py-3 border border-slate-200 rounded-xl hover:border-[#2EB4AC]/40 hover:bg-[#2EB4AC]/[0.02] transition-all">
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2EB4AC]/15 to-cyan-100 flex items-center justify-center shrink-0">
        <Building2 className="h-5 w-5 text-[#2EB4AC]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-slate-900 truncate">{item.brand_name}</p>
          {item.schedule_frequency !== 'off' && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] py-0 h-5">{FREQ_LABELS[item.schedule_frequency]}</Badge>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-0.5 flex-wrap">
          <span className="flex items-center gap-1"><StatusDot status={item.last_pull_status} />{item.last_pull_status || 'never pulled'}</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{last}</span>
          <span>{item.last_pull_count ?? 0} ads</span>
          {item.media_type && item.media_type !== 'all' && (
            <Badge variant="outline" className="h-4 text-[9px] py-0 px-1.5 border-slate-200 capitalize">
              {item.media_type === 'image' ? 'Static' : item.media_type}
            </Badge>
          )}
          {(item.date_from || item.date_to) && (
            <Badge variant="outline" className="h-4 text-[9px] py-0 px-1.5 border-slate-200">
              {item.date_from || '…'} → {item.date_to || 'today'}
            </Badge>
          )}
          {item.page_id && <span className="truncate">PID {item.page_id}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="outline" onClick={() => onPull(item)} disabled={isPulling} data-testid={`pull-btn-${item.id}`}>
                {isPulling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Pull ads now</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" onClick={() => onEdit(item)}><Edit3 className="h-4 w-4" /></Button>
            </TooltipTrigger>
            <TooltipContent>Edit</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" onClick={() => onDelete(item)} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></Button>
            </TooltipTrigger>
            <TooltipContent>Remove</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

// Helpers
const PLATFORM_ICON = {
  FACEBOOK: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  INSTAGRAM: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z',
  MESSENGER: 'M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.744 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.974 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z',
  THREADS: 'M17.25 12.08a6.25 6.25 0 0 0-5.063-6.138c-.093-.017-.189-.03-.285-.04-.093-.01-.188-.017-.284-.02-.064-.005-.132-.007-.198-.008a6.25 6.25 0 0 0 0 12.503c.09 0 .178-.003.265-.006.046-.004.093-.005.14-.008.115-.005.232-.013.343-.024a6.25 6.25 0 0 0 5.082-6.26z',
  AUDIENCE_NETWORK: 'M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm1.2 21.6H10.8V10.8h2.4v10.8zM12 7.2a1.8 1.8 0 1 1 0-3.6 1.8 1.8 0 0 1 0 3.6z',
};

const PlatformIcon = ({ name }) => {
  const d = PLATFORM_ICON[(name || '').toUpperCase()];
  if (!d) return null;
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-slate-500 inline-block" fill="currentColor">
      <path d={d} />
    </svg>
  );
};

const fmtRunningDate = (d) => {
  if (!d) return null;
  try {
    const dt = typeof d === 'number' ? new Date(d * 1000) : new Date(String(d));
    return format(dt, 'd MMM yyyy');
  } catch { return null; }
};

const calcActiveTime = (started, ended) => {
  if (!started) return null;
  try {
    const start = typeof started === 'number' ? new Date(started * 1000) : new Date(String(started));
    const end = ended ? (typeof ended === 'number' ? new Date(ended * 1000) : new Date(String(ended))) : new Date();
    const hrs = Math.max(0, Math.floor((end - start) / (1000 * 60 * 60)));
    if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'}`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'}`;
    const months = Math.floor(days / 30);
    return `${months} month${months === 1 ? '' : 's'}`;
  } catch { return null; }
};

// Returns just the number of full days an ad has been running (null if unknown).
const calcActiveDays = (started, ended) => {
  if (!started) return null;
  try {
    const start = typeof started === 'number' ? new Date(started * 1000) : new Date(String(started));
    const end = ended ? (typeof ended === 'number' ? new Date(ended * 1000) : new Date(String(ended))) : new Date();
    const days = Math.max(0, Math.floor((end - start) / (1000 * 60 * 60 * 24)));
    return days;
  } catch { return null; }
};

const getLandingDomain = (link) => {
  if (!link) return null;
  try {
    const u = new URL(link);
    return (u.hostname || '').replace(/^www\./, '').toUpperCase();
  } catch { return null; }
};

// Futuristic AI Extraction Modal — shows during ad pulls
const ExtractionStages = [
  { key: 'connect', label: 'Establishing secure channel', sublabel: 'TLS · HTTP/2 · residential proxy routing', icon: Shield, duration: 2000, color: 'cyan' },
  { key: 'meta', label: 'Querying Meta Ad Library', sublabel: 'facebook.com/ads/library · active ads only', icon: Globe, duration: 4000, color: 'blue' },
  { key: 'apify', label: 'Running Apify scraping actor', sublabel: 'curious_coder/facebook-ads-library-scraper', icon: Zap, duration: 14000, color: 'violet' },
  { key: 'parse', label: 'Parsing creative payloads', sublabel: 'extracting images · videos · CTAs · platforms', icon: Cpu, duration: 6000, color: 'fuchsia' },
  { key: 'dedupe', label: 'De-duplicating against library', sublabel: 'MongoDB upsert · idempotent on ad_archive_id', icon: Database, duration: 3000, color: 'emerald' },
  { key: 'ready', label: 'Indexing for intelligent retrieval', sublabel: 'ready for GPT-4o Vision analysis', icon: Brain, duration: 2500, color: 'teal' },
];

const ExtractionModal = ({ open, keyword, mediaType, sortBy, maxItems }) => {
  const [stageIdx, setStageIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  React.useEffect(() => {
    if (!open) { setStageIdx(0); setElapsed(0); return; }
    setStageIdx(0);
    setElapsed(0);
    let cancelled = false;
    const timers = [];
    let acc = 0;
    ExtractionStages.forEach((stage, i) => {
      acc += stage.duration;
      const t = setTimeout(() => {
        if (!cancelled) setStageIdx(Math.min(i + 1, ExtractionStages.length - 1));
      }, acc);
      timers.push(t);
    });
    const interval = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => { cancelled = true; timers.forEach(clearTimeout); clearInterval(interval); };
  }, [open]);

  if (!open) return null;

  const currentStage = ExtractionStages[stageIdx];
  const totalStages = ExtractionStages.length;
  const CurrentIcon = currentStage.icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 backdrop-blur-md" data-testid="extraction-modal">
      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#2EB4AC] opacity-20 blur-[120px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-violet-500 opacity-20 blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative w-full max-w-lg mx-4 rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden">
        {/* Top gradient bar */}
        <div className="h-1 bg-gradient-to-r from-[#2EB4AC] via-cyan-400 via-violet-500 to-fuchsia-500 animate-pulse" />

        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#2EB4AC] to-violet-500 opacity-60 blur-lg animate-pulse" />
              <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-[#2EB4AC] to-violet-500 flex items-center justify-center">
                <Radar className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold tracking-[0.2em] text-cyan-400 uppercase">PRISM · Competitor Radar</p>
              <p className="text-lg font-bold text-white truncate">Extracting "{keyword || '...'}"</p>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            India · {mediaType === 'video' ? 'video creatives' : 'all creative types'} · up to {maxItems} · {sortBy === 'recent' ? 'newest first' : 'highest impressions'}
          </p>
        </div>

        {/* Current stage hero */}
        <div className="px-6 pb-4">
          <div className="rounded-xl bg-slate-800/60 border border-slate-700/50 p-4 flex items-center gap-4 relative overflow-hidden">
            {/* animated scan line */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-y-0 w-32 bg-gradient-to-r from-transparent via-cyan-400/10 to-transparent"
                style={{ animation: 'scan 2.2s linear infinite' }} />
            </div>
            <div className="relative shrink-0 w-11 h-11 rounded-lg bg-gradient-to-br from-[#2EB4AC]/30 to-violet-500/30 border border-cyan-400/30 flex items-center justify-center">
              <CurrentIcon className="h-5 w-5 text-cyan-300" />
              <div className="absolute inset-0 rounded-lg border border-cyan-400 animate-ping opacity-40" />
            </div>
            <div className="flex-1 min-w-0 relative">
              <p className="text-[10px] font-semibold tracking-widest text-cyan-400 uppercase">Stage {stageIdx + 1} / {totalStages}</p>
              <p className="text-sm font-semibold text-white truncate">{currentStage.label}</p>
              <p className="text-[11px] text-slate-400 truncate">{currentStage.sublabel}</p>
            </div>
            <div className="text-right relative">
              <p className="text-[10px] text-slate-500">Elapsed</p>
              <p className="text-sm font-mono font-semibold text-cyan-300 tabular-nums">{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}</p>
            </div>
          </div>
        </div>

        {/* Pipeline list */}
        <div className="px-6 pb-4 space-y-1.5">
          {ExtractionStages.map((s, i) => {
            const done = i < stageIdx;
            const active = i === stageIdx;
            const pending = i > stageIdx;
            const Icon = s.icon;
            return (
              <div
                key={s.key}
                className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md transition-all ${
                  active ? 'bg-cyan-500/10' : done ? 'bg-slate-800/40' : 'bg-transparent'
                }`}
              >
                <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${
                  done ? 'bg-emerald-500/20 text-emerald-400' :
                  active ? 'bg-cyan-400/20 text-cyan-300' :
                  'bg-slate-800 text-slate-600'
                }`}>
                  {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : active ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3 w-3" />}
                </div>
                <p className={`text-[12px] flex-1 truncate ${
                  done ? 'text-slate-500 line-through' :
                  active ? 'text-white font-medium' :
                  'text-slate-500'
                }`}>{s.label}</p>
                {active && <span className="text-[9px] font-mono text-cyan-400 animate-pulse">RUNNING</span>}
                {done && <span className="text-[9px] font-mono text-emerald-400">OK</span>}
                {pending && <span className="text-[9px] font-mono text-slate-600">QUEUED</span>}
              </div>
            );
          })}
        </div>

        {/* Tech chips footer */}
        <div className="px-6 py-4 bg-slate-950/50 border-t border-slate-800">
          <p className="text-[9px] font-bold tracking-[0.2em] text-slate-500 uppercase mb-2">Technology stack engaged</p>
          <div className="flex flex-wrap gap-1.5">
            {['Apify', 'Meta Graph', 'FastAPI', 'MongoDB', 'GPT-4o Vision', 'HTTPX async'].map((t, i) => (
              <span
                key={t}
                className="text-[10px] px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-slate-300 font-mono"
                style={{ animation: `pulse 2s ease-in-out ${i * 0.2}s infinite` }}
              >
                {t}
              </span>
            ))}
          </div>
          <div className="mt-3 h-1 rounded-full bg-slate-800 overflow-hidden relative">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#2EB4AC] via-cyan-400 to-violet-400 transition-all duration-1000 ease-out"
              style={{ width: `${Math.min(100, ((stageIdx + 1) / totalStages) * 100)}%` }}
            />
            <div className="absolute inset-0 opacity-40 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)]"
              style={{ animation: 'shimmer 1.8s linear infinite' }} />
          </div>
        </div>
      </div>

      {/* Inline keyframes */}
      <style>{`
        @keyframes scan {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

const AdCard = ({ ad, onAnalyze, onView, onDelete, analyzing, selected, onToggleSelect, selectionMode }) => {
  const thumb = ad.image_urls?.[0] || null;
  const videoThumb = ad.video_urls?.[0] || null;
  const posterThumb = ad.poster_url || null;
  const hasAnalysis = !!ad.analysis;
  // Any signal that this is a video ad (local URL, remote URL, or backend flag)
  const isVideo = !!(videoThumb || ad.video_urls_remote?.length || ad.is_video);
  const isLowImpressions = ad.impressions_max && Number(ad.impressions_max) <= 1000;
  const startedLabel = fmtRunningDate(ad.started_on);
  const activeTime = calcActiveTime(ad.started_on, ad.ended_on);
  const activeDays = calcActiveDays(ad.started_on, ad.ended_on);
  const landingDomain = getLandingDomain(ad.cta_link);
  const platforms = Array.from(new Set(ad.platforms || [])).slice(0, 6);

  const handleCardClick = (e) => {
    if (selectionMode) {
      e.stopPropagation();
      onToggleSelect(ad.id);
      return;
    }
    onView(ad);
  };

  return (
    <div
      className={`group relative rounded-2xl bg-white transition-all flex flex-col overflow-hidden ${selected ? 'ring-2 ring-[#2EB4AC] shadow-[0_4px_16px_-4px_rgba(46,180,172,0.35)]' : 'border border-slate-200/80 hover:shadow-[0_6px_20px_-8px_rgba(15,23,42,0.15)] hover:border-slate-300'}`}
      data-testid={`competitor-ad-card-${ad.id}`}
    >
      {/* Selection checkbox — overlays top-right */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleSelect(ad.id); }}
        className={`absolute top-3 right-3 z-20 w-6 h-6 rounded-md flex items-center justify-center transition-all ${
          selected
            ? 'bg-[#2EB4AC] text-white shadow-md opacity-100'
            : 'bg-white/95 text-slate-400 border border-slate-200 opacity-0 group-hover:opacity-100 hover:text-[#2EB4AC] hover:border-[#2EB4AC]'
        }`}
        aria-label={selected ? 'Deselect' : 'Select'}
        data-testid={`select-ad-${ad.id}`}
      >
        {selected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
      </button>

      {/* === TOP: Status + meta info (Meta's header block) === */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
          </span>
          {ad.is_new_pull && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#2EB4AC]/15 text-[#2EB4AC] text-[11px] font-bold border border-[#2EB4AC]/30">
              <Sparkles className="h-2.5 w-2.5" /> NEW
            </span>
          )}
          {isLowImpressions && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-50 text-orange-700 text-[11px] font-semibold">
              Low impression count
            </span>
          )}
          {hasAnalysis && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 text-[11px] font-semibold">
              <Sparkles className="h-2.5 w-2.5" /> Analyzed
            </span>
          )}
        </div>

        {ad.ad_archive_id && (
          <p className="text-[12px] text-slate-700 mb-1.5 tracking-tight">
            <span className="font-medium text-slate-500">Library ID</span> <span className="font-mono text-slate-800">{ad.ad_archive_id}</span>
          </p>
        )}

        {startedLabel && (
          <p className="text-[12px] text-slate-600 mb-2.5 leading-snug">
            Started running on <span className="font-medium text-slate-700">{startedLabel}</span>
          </p>
        )}

        {platforms.length > 0 && (
          <div className="flex items-center gap-1.5 mb-2.5">
            <span className="text-[12px] text-slate-500">Platforms</span>
            <div className="flex items-center gap-1">
              {platforms.map(p => <PlatformIcon key={p} name={p} />)}
            </div>
          </div>
        )}

        {ad.impressions_max != null && (
          <p className="text-[12px] text-slate-600 mb-2.5">
            <Eye className="h-3 w-3 inline mr-1 text-slate-400" />
            Impressions: <span className="font-semibold text-slate-800">
              {ad.impressions_min ? `${Number(ad.impressions_min).toLocaleString()}–${Number(ad.impressions_max).toLocaleString()}` : `<${Number(ad.impressions_max).toLocaleString()}`}
            </span>
          </p>
        )}

        <button
          onClick={handleCardClick}
          className="w-full mt-1 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-[13px] font-semibold text-slate-700 transition-colors"
          data-testid={`see-details-${ad.id}`}
        >
          See ad details
        </button>
      </div>

      {/* Divider */}
      <div className="border-t border-slate-100" />

      {/* === MIDDLE: Sponsor header === */}
      <div className="px-4 pt-3 pb-2.5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-[11px] font-bold text-slate-600 shrink-0">
            {(ad.page_name || ad.source_brand || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-slate-900 truncate leading-tight">{ad.page_name || ad.source_brand}</p>
            <p className="text-[11px] text-slate-500">Sponsored</p>
          </div>
        </div>
        {ad.body_text && (
          <p className="text-[12px] text-slate-700 mt-2 leading-relaxed line-clamp-3 whitespace-pre-wrap">{ad.body_text}</p>
        )}
      </div>

      {/* === MEDIA === */}
      <div className="relative bg-slate-900 cursor-pointer overflow-hidden" onClick={handleCardClick}>
        {thumb ? (
          <img alt="" src={thumb} referrerPolicy="no-referrer" className="w-full aspect-[4/5] object-cover" />
        ) : posterThumb ? (
          // Durable server-generated poster — permanent thumbnail
          <img alt="" src={posterThumb} referrerPolicy="no-referrer" className="w-full aspect-[4/5] object-cover" />
        ) : videoThumb ? (
          <video
            src={`${videoThumb}#t=0.5`}
            preload="metadata"
            muted
            playsInline
            className="w-full aspect-[4/5] object-cover pointer-events-none bg-gradient-to-br from-slate-800 to-slate-900"
            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement?.classList.add('video-expired'); }}
          />
        ) : (
          <div className="w-full aspect-[4/5] flex items-center justify-center bg-slate-100"><ImageIcon className="h-10 w-10 text-slate-300" /></div>
        )}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-14 h-14 rounded-full border-[3px] border-white bg-white/10 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <Play className="h-5 w-5 text-white fill-white ml-0.5" />
            </div>
          </div>
        )}

        {/* Bottom gradient scrim for badge legibility */}
        {(activeDays != null || thumb || videoThumb) && (
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/55 via-black/15 to-transparent pointer-events-none" />
        )}

        {/* Bottom-left: Active-time flame badge */}
        {activeDays != null && (
          <div
            className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/95 backdrop-blur-sm shadow-sm text-[11px] font-bold text-slate-800"
            title={activeTime ? `Total active time: ${activeTime}` : ''}
            data-testid={`active-days-badge-${ad.id}`}
          >
            <span className="text-[12px] leading-none">🔥</span>
            <span>{activeDays} {activeDays === 1 ? 'day' : 'days'}</span>
          </div>
        )}

        {/* Bottom-right: Media type chip */}
        <div
          className="absolute bottom-2.5 right-2.5 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm text-[10px] font-bold tracking-wider text-white uppercase"
          data-testid={`media-type-chip-${ad.id}`}
        >
          {isVideo ? (
            <>
              <Video className="h-3 w-3" /> Video
            </>
          ) : (
            <>
              <ImageIcon className="h-3 w-3" /> Image
            </>
          )}
        </div>
      </div>

      {/* === FOOTER: Landing URL + CTA === */}
      <div className="px-4 py-2.5 bg-slate-50/60 flex items-center justify-between gap-2 border-t border-slate-100">
        <div className="min-w-0 flex-1">
          {landingDomain && (
            <p className="text-[10px] text-slate-500 font-medium tracking-wider truncate">{landingDomain}</p>
          )}
          {ad.cta_text && !landingDomain && (
            <p className="text-[11px] text-slate-600 truncate">{ad.page_name || ad.source_brand}</p>
          )}
        </div>
        {ad.cta_text && (
          <button
            onClick={(e) => { e.stopPropagation(); if (ad.cta_link) window.open(ad.cta_link, '_blank', 'noreferrer'); }}
            className="shrink-0 px-3 py-1.5 rounded-md bg-slate-200 hover:bg-slate-300 text-[12px] font-semibold text-slate-700"
          >
            {ad.cta_text}
          </button>
        )}
      </div>

      {/* Action strip on hover — bottom */}
      <div className="flex items-center justify-between gap-1 px-2 py-1.5 border-t border-slate-100 bg-white">
        {hasAnalysis ? (
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-[#2EB4AC]" onClick={(e) => { e.stopPropagation(); onView(ad); }}>
            <Eye className="h-3 w-3 mr-1" /> View insights
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            disabled={analyzing || !thumb}
            onClick={(e) => { e.stopPropagation(); onAnalyze(ad); }}
            className="h-7 text-[11px] text-violet-600 hover:bg-violet-50"
            data-testid={`analyze-btn-${ad.id}`}
          >
            {analyzing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
            Analyze with AI
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); onDelete(ad); }}
          className="h-7 w-7 text-slate-400 hover:text-rose-500 hover:bg-rose-50"
          data-testid={`delete-ad-${ad.id}`}
          title="Delete this ad"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

const fmtDate = (d) => {
  if (!d) return '—';
  if (typeof d === 'number') return format(new Date(d * 1000), 'MMM d, yyyy');
  const s = String(d).slice(0, 10);
  try { return format(new Date(s), 'MMM d, yyyy'); } catch { return s; }
};

export default function CompetitorRadarPage() {
  const [watchlist, setWatchlist] = useState([]);
  const [ads, setAds] = useState([]);
  const [totalAds, setTotalAds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pullingId, setPullingId] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [brandFilter, setBrandFilter] = useState('all');
  const [mediaFilter, setMediaFilter] = useState('all');
  const [activeTimeFilter, setActiveTimeFilter] = useState('any'); // any | lt7 | 7to30 | 30to90 | gt90
  const [analyzedFilter, setAnalyzedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent'); // recent | oldest | impressions

  // Client-side re-sort — makes the Sort chip instant even after a scrape has completed.
  // The backend already asks Apify to sort at fetch time; this just keeps the UI in sync
  // if the user changes the sort choice without refetching.
  const displayedAds = useMemo(() => {
    if (!Array.isArray(ads) || ads.length === 0) return [];
    const pickTs = (a) => {
      const raw = a.start_date_iso || a.first_seen_at || a.created_at || a.scraped_at || 0;
      const t = raw ? new Date(raw).getTime() : 0;
      return Number.isNaN(t) ? 0 : t;
    };
    const pickImpr = (a) => {
      const raw = a.impressions_upper_bound ?? a.impressions ?? a.impressions_lower_bound ?? 0;
      const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
      return Number.isNaN(n) ? 0 : n;
    };
    let copy = [...ads];
    // Client-side media filter (segmented All / Image / Video)
    const isVid = (a) => !!(a.video_urls?.length || a.video_urls_remote?.length || a.is_video);
    if (mediaFilter === 'image') {
      copy = copy.filter(a => !isVid(a));
    } else if (mediaFilter === 'video') {
      copy = copy.filter(a => isVid(a));
    }
    // Client-side active-time filter
    if (activeTimeFilter !== 'any') {
      copy = copy.filter(a => {
        const d = calcActiveDays(a.started_on, a.ended_on);
        if (d == null) return false;
        if (activeTimeFilter === 'lt7') return d < 7;
        if (activeTimeFilter === '7to30') return d >= 7 && d < 30;
        if (activeTimeFilter === '30to90') return d >= 30 && d < 90;
        if (activeTimeFilter === 'gt90') return d >= 90;
        return true;
      });
    }
    if (sortBy === 'impressions') {
      copy.sort((a, b) => pickImpr(b) - pickImpr(a) || pickTs(b) - pickTs(a));
    } else {
      // 'recent' — newest first
      copy.sort((a, b) => pickTs(b) - pickTs(a));
    }
    return copy;
  }, [ads, sortBy, mediaFilter, activeTimeFilter]);
  const [selectedAd, setSelectedAd] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [applyBrandOpen, setApplyBrandOpen] = useState(false);
  const [applyBrandValue, setApplyBrandValue] = useState('');
  const [applyingBrand, setApplyingBrand] = useState(false);
  const [competitorBrands, setCompetitorBrands] = useState([]);

  // Meta-style inline search state
  const [quickKeyword, setQuickKeyword] = useState('');
  const [quickCategory, setQuickCategory] = useState('all');
  const [quickCountry] = useState('IN');
  const [quickMedia, setQuickMedia] = useState('all'); // all | video (kept for legacy URL params but hidden from UI)
  const [quickDateRange, setQuickDateRange] = useState('any'); // any | 7 | 30 | 90 | custom
  const [quickDateFrom, setQuickDateFrom] = useState('');
  const [quickDateTo, setQuickDateTo] = useState('');
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickMaxItems, setQuickMaxItems] = useState(200); // internal — no UI toggle; we always fetch a wide net
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestions, setSuggestions] = useState({ recent: [], advertisers: [] });
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [quickPagePick, setQuickPagePick] = useState(null);

  const fetchWatchlist = async () => {
    try {
      const r = await fetch(`${API_URL}/api/competitors`, { credentials: 'include' });
      if (!r.ok) throw new Error();
      const j = await r.json();
      setWatchlist(j || []);
    } catch {
      toast.error('Could not load watchlist');
    }
  };

  const fetchAds = async () => {
    const params = new URLSearchParams();
    if (brandFilter !== 'all') params.set('brand', brandFilter);
    // Fetch ALL media types — the segmented All/Image/Video filter is applied
    // entirely client-side on the results grid. Previously this hard-coded
    // media=image, which made backend hide every video ad from the UI.
    if (analyzedFilter !== 'all') params.set('analyzed', analyzedFilter);
    params.set('sort', sortBy);
    params.set('limit', '96');
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads?${params.toString()}`, { credentials: 'include' });
      if (!r.ok) throw new Error();
      const j = await r.json();
      setAds(j.items || []);
      setTotalAds(j.total || 0);
    } catch {
      toast.error('Could not load ads');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchWatchlist(), fetchAds()]);
      setLoading(false);
    })();
    // load previously-used competitor brand tags (separate from own-brand master)
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/competitor-ads/brands`, { credentials: 'include' });
        if (r.ok) {
          const j = await r.json();
          setCompetitorBrands(j.brands || []);
        }
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandFilter, quickMedia, analyzedFilter, sortBy]);

  // Debounced suggestions fetcher
  useEffect(() => {
    if (!showSuggest) return;
    const t = setTimeout(async () => {
      setSuggestBusy(true);
      try {
        const r = await fetch(`${API_URL}/api/competitors/suggestions?q=${encodeURIComponent(quickKeyword || '')}`, { credentials: 'include' });
        if (r.ok) {
          const j = await r.json();
          setSuggestions({ recent: j.recent || [], advertisers: j.advertisers || [] });
        }
      } catch {
        // silent
      } finally {
        setSuggestBusy(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [quickKeyword, showSuggest]);

  const pickSuggestion = (label, pageId) => {
    setShowSuggest(false);
    setQuickKeyword(label);
    // Store the picked page_id so the next Search click uses it for precision
    setQuickPagePick(pageId || null);
  };

  const runQuickSearchWith = async ({ keyword, pageId }) => {
    const kw = (keyword || '').trim();
    if (!kw) { toast.error('Type a brand or keyword first'); return; }
    setQuickBusy(true);
    const loadingToast = toast.loading(`Searching Meta Ad Library for "${kw}"…`);
    try {
      let df = null, dt = null;
      if (quickDateRange === 'custom') { df = quickDateFrom || null; dt = quickDateTo || null; }
      else if (quickDateRange !== 'any') {
        const days = parseInt(quickDateRange, 10);
        const d = new Date(); d.setDate(d.getDate() - days);
        df = d.toISOString().slice(0, 10);
        dt = new Date().toISOString().slice(0, 10);
      }
      let existing = watchlist.find(w =>
        (w.brand_name || '').toLowerCase() === kw.toLowerCase() ||
        (w.search_keyword || '').toLowerCase() === kw.toLowerCase()
      );
      // Always ask Meta for 'all' (broadest match, mirrors their default). We filter
      // videos on our side when the user picks "All (no video)" — avoids Meta's
      // over-strict media_type=image filter that sometimes returns 0.
      // Always request both images + videos; the UI has no scrape-side media filter anymore.
      const metaMediaType = 'all';
      const body = {
        brand_name: kw, search_keyword: kw,
        page_id: pageId || null,
        country: quickCountry,
        media_type: metaMediaType, date_from: df, date_to: dt,
        sort_by: sortBy,
        ui_media_preference: quickMedia,
        max_items: quickMaxItems, schedule_frequency: 'off',
      };
      if (!existing) {
        const r = await fetch(`${API_URL}/api/competitors`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Could not save search');
        existing = await r.json();
      } else {
        await fetch(`${API_URL}/api/competitors/${existing.id}`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ page_id: pageId || null, media_type: metaMediaType, date_from: df, date_to: dt, sort_by: sortBy, max_items: quickMaxItems }),
        });
      }
      const pr = await fetch(`${API_URL}/api/competitors/${existing.id}/pull`, { method: 'POST', credentials: 'include' });
      const pj = await pr.json();
      if (!pr.ok) throw new Error(pj.detail || 'Pull failed');
      toast.success(`Found ${pj.total_fetched} ads for "${kw}"`, { id: loadingToast });
      setQuickKeyword('');
      setBrandFilter(existing.brand_name);
      await Promise.all([fetchWatchlist(), fetchAds()]);
    } catch (e) {
      toast.error(`Search failed: ${e.message}`, { id: loadingToast });
    } finally {
      setQuickBusy(false);
    }
  };

  const saveCompetitor = async (form) => {
    const isEdit = !!editingItem;
    const url = isEdit ? `${API_URL}/api/competitors/${editingItem.id}` : `${API_URL}/api/competitors`;
    const method = isEdit ? 'PUT' : 'POST';
    try {
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success(isEdit ? 'Competitor updated' : 'Competitor added');
      setFormOpen(false);
      setEditingItem(null);
      fetchWatchlist();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const pullNow = async (item) => {
    setPullingId(item.id);
    const loadingToast = toast.loading(`Pulling ads for ${item.brand_name}…`);
    try {
      const r = await fetch(`${API_URL}/api/competitors/${item.id}/pull`, {
        method: 'POST', credentials: 'include',
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Pull failed');
      toast.success(`Pulled ${j.total_fetched} ads (${j.new_saved} new)`, { id: loadingToast });
      await Promise.all([fetchWatchlist(), fetchAds()]);
    } catch (e) {
      toast.error(`Pull failed: ${e.message}`, { id: loadingToast });
    } finally {
      setPullingId(null);
    }
  };

  const deleteCompetitor = async (item) => {
    if (!window.confirm(`Remove ${item.brand_name} from watchlist? Existing ads stay.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/competitors/${item.id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error();
      toast.success('Removed');
      fetchWatchlist();
    } catch {
      toast.error('Failed to remove');
    }
  };

  // One-click search: creates a saved search + immediately pulls ads
  const runQuickSearch = () => runQuickSearchWith({ keyword: quickKeyword, pageId: quickPagePick });

  const analyzeAd = async (ad) => {
    setAnalyzingId(ad.id);
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/${ad.id}/analyze`, { method: 'POST', credentials: 'include' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Analysis failed');
      toast.success('Analyzed with GPT-4o');
      // patch locally (functional updates — no stale closure issues)
      setAds(prev => prev.map(x => x.id === ad.id ? { ...x, analysis: j.analysis, analysis_id: j.analysis_id } : x));
      setSelectedAd(prev => (prev && prev.id === ad.id) ? { ...prev, analysis: j.analysis, analysis_id: j.analysis_id } : prev);
    } catch (e) {
      toast.error(`Analysis failed: ${e.message}`);
    } finally {
      setAnalyzingId(null);
    }
  };

  const deleteAd = async (ad) => {
    if (!window.confirm('Delete this ad from PRISM?')) return;
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/${ad.id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error();
      setAds(prev => prev.filter(x => x.id !== ad.id));
      setSelectedIds(prev => prev.filter(id => id !== ad.id));
      setTotalAds(prev => Math.max(0, prev - 1));
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllVisible = () => {
    const visibleIds = ads.map(a => a.id);
    const allSelected = visibleIds.every(id => selectedIds.includes(id));
    if (allSelected) setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
    else setSelectedIds(prev => [...new Set([...prev, ...visibleIds])]);
  };

  const clearSelection = () => setSelectedIds([]);

  const applyBrandToSelected = async () => {
    const brand = applyBrandValue.trim();
    if (!brand || selectedIds.length === 0) return;
    setApplyingBrand(true);
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/apply-brand`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds, brand }),
      });
      if (!r.ok) throw new Error();
      const j = await r.json();
      toast.success(`${j.updated} ads filed to "${brand}" repository`);
      // Remove from gallery (since default is unassigned) + clear selection
      setAds(prev => prev.filter(x => !selectedIds.includes(x.id)));
      setTotalAds(prev => Math.max(0, prev - j.updated));
      setSelectedIds([]);
      setApplyBrandOpen(false);
      setApplyBrandValue('');
      // Optimistically add/increment this brand in the competitor-brand typeahead list
      setCompetitorBrands(prev => {
        const existing = prev.find(b => b.name.toLowerCase() === brand.toLowerCase());
        if (existing) {
          return [{ ...existing, count: existing.count + j.updated }, ...prev.filter(b => b !== existing)];
        }
        return [{ name: brand, count: j.updated }, ...prev];
      });
    } catch {
      toast.error('Apply brand failed');
    } finally {
      setApplyingBrand(false);
    }
  };

  const saveCompetitorLearning = async (adId, learningText) => {
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/${adId}/learning`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ learning: learningText }),
      });
      if (!r.ok) throw new Error();
      toast.success('Learning saved');
      const now = new Date().toISOString();
      setAds(prev => prev.map(x => x.id === adId ? { ...x, learning: learningText, learning_updated_at: now } : x));
      setSelectedAd(prev => prev && prev.id === adId ? { ...prev, learning: learningText, learning_updated_at: now } : prev);
    } catch {
      toast.error('Could not save learning');
    }
  };

  const rephraseCompetitorLearning = async (adId) => {
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/${adId}/rephrase-learning`, {
        method: 'POST', credentials: 'include',
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Rephrase failed');
      toast.success('Actionable summary generated');
      setAds(prev => prev.map(x => x.id === adId ? { ...x, learning_rephrased: j.learning_rephrased, learning_rephrased_at: j.learning_rephrased_at } : x));
      setSelectedAd(prev => prev && prev.id === adId ? { ...prev, learning_rephrased: j.learning_rephrased, learning_rephrased_at: j.learning_rephrased_at } : prev);
    } catch (e) {
      toast.error(`Rephrase failed: ${e.message || ''}`);
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0 || bulkDeleting) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected ${selectedIds.length === 1 ? 'ad' : 'ads'}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const r = await fetch(`${API_URL}/api/competitor-ads/bulk-delete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds }),
      });
      if (!r.ok) throw new Error();
      const j = await r.json();
      toast.success(`${j.deleted} ads deleted`);
      setAds(prev => prev.filter(x => !selectedIds.includes(x.id)));
      setTotalAds(prev => Math.max(0, prev - j.deleted));
      setSelectedIds([]);
    } catch {
      toast.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const brands = useMemo(() => [...new Set(watchlist.map(w => w.brand_name))], [watchlist]);
  const analyzedCount = useMemo(() => ads.filter(a => a.analysis).length, [ads]);
  const videoCount = useMemo(() => ads.filter(a => !!(a.video_urls?.length || a.video_urls_remote?.length || a.is_video)).length, [ads]);
  const imageCount = useMemo(() => ads.length - videoCount, [ads, videoCount]);
  // Active-time bucket counts (source of truth for the dropdown labels).
  const activeTimeCounts = useMemo(() => {
    const b = { lt7: 0, '7to30': 0, '30to90': 0, gt90: 0 };
    for (const a of ads) {
      const d = calcActiveDays(a.started_on, a.ended_on);
      if (d == null) continue;
      if (d < 7) b.lt7++;
      else if (d < 30) b['7to30']++;
      else if (d < 90) b['30to90']++;
      else b.gt90++;
    }
    return b;
  }, [ads]);
  const activeTimeLabel = {
    any: 'Any duration',
    lt7: '< 7 days',
    '7to30': '7 – 30 days',
    '30to90': '30 – 90 days',
    gt90: '90+ days',
  };

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="competitor-radar-page">
      {/* Page title */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#2EB4AC] to-cyan-500 flex items-center justify-center shadow-lg shadow-[#2EB4AC]/20">
            <Radar className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Competitor Radar</h1>
            <p className="text-sm text-slate-500">Track competitor ads from Meta's Ad Library · analyze with GPT-4o</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{watchlist.length} saved</span>
          <span className="inline-flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" />{totalAds} ads</span>
          <span className="inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-violet-500" />{analyzedCount} analyzed</span>
        </div>
      </div>

      {/* Meta-style Search Ads card */}
      <Card className="border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 md:p-8">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Search ads</h2>
          <p className="text-sm text-slate-500 mb-6">
            Set your location and choose an ad category to start your search.{' '}
            <a href="https://www.facebook.com/ads/library/search_tips" target="_blank" rel="noreferrer" className="font-semibold text-slate-900 underline hover:text-[#2EB4AC]">View search tips.</a>
          </p>

          {/* Search row: Country pill + Ad category pill + Search input */}
          <div className="flex items-stretch gap-2 flex-wrap md:flex-nowrap">
            {/* Country pill */}
            <button
              type="button"
              className="inline-flex items-center gap-2 h-12 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
              data-testid="country-pill"
            >
              <span className="text-base">🇮🇳</span> India
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>

            {/* Ad category pill */}
            <Select value={quickCategory} onValueChange={setQuickCategory}>
              <SelectTrigger
                className="h-12 min-w-[150px] w-auto px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 gap-2"
                data-testid="category-pill"
              >
                <SelectValue placeholder="All ads" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ads</SelectItem>
                <SelectItem value="issues">Issues, elections or politics</SelectItem>
                <SelectItem value="housing">Housing ads</SelectItem>
                <SelectItem value="employment">Employment ads</SelectItem>
                <SelectItem value="financial">Financial products & services</SelectItem>
              </SelectContent>
            </Select>

            {/* Search input with typeahead dropdown */}
            <div className="flex-1 min-w-[240px] relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none z-10" />
              <Input
                value={quickKeyword}
                onChange={(e) => { setQuickKeyword(e.target.value); setQuickPagePick(null); }}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setShowSuggest(false); runQuickSearch(); } if (e.key === 'Escape') setShowSuggest(false); }}
                placeholder="Search by keyword or advertiser (e.g. MuscleBlaze, Nike)"
                className="h-12 pl-11 pr-4 rounded-xl border-slate-200 bg-slate-50/70 focus-visible:ring-[#2EB4AC]/30 focus-visible:border-[#2EB4AC] text-sm relative"
                data-testid="quick-search-input"
                disabled={quickBusy}
              />

              {/* Typeahead dropdown — Meta-style */}
              {showSuggest && (suggestions.recent.length > 0 || suggestions.advertisers.length > 0 || quickKeyword.trim()) && (
                <div
                  className="absolute top-full left-0 right-0 mt-1.5 rounded-xl bg-white border border-slate-200 shadow-xl z-50 max-h-[420px] overflow-y-auto"
                  onMouseDown={(e) => e.preventDefault()}
                  data-testid="search-suggest-dropdown"
                >
                  {/* Recent searches */}
                  {suggestions.recent.length > 0 && (
                    <div className="px-4 pt-3 pb-2">
                      <p className="text-[11px] font-bold text-slate-500 tracking-wide mb-2">Recent searches</p>
                      <div className="space-y-0.5">
                        {suggestions.recent.map((r, i) => (
                          <button
                            key={`r-${i}`}
                            onClick={() => pickSuggestion(r.label, null)}
                            className="w-full text-left px-2 py-1.5 rounded-md hover:bg-slate-50 text-sm text-slate-700 flex items-center gap-2"
                            data-testid={`recent-suggest-${i}`}
                          >
                            <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            <span className="truncate">{r.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Keyword chips — always shown when typing */}
                  {quickKeyword.trim() && (
                    <div className="px-4 pt-2 pb-2 border-t border-slate-100">
                      <div className="space-y-0.5">
                        <button
                          onClick={() => pickSuggestion(quickKeyword.trim(), null)}
                          className="w-full text-left px-2 py-1.5 rounded-md hover:bg-slate-50 text-sm text-slate-700 flex items-center gap-2"
                          data-testid="keyword-suggest-loose"
                        >
                          <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          <span className="truncate">{quickKeyword.trim()}</span>
                        </button>
                        <button
                          onClick={() => pickSuggestion(`"${quickKeyword.trim()}"`, null)}
                          className="w-full text-left px-2 py-1.5 rounded-md hover:bg-slate-50 text-sm text-slate-700 flex items-center gap-2"
                          data-testid="keyword-suggest-exact"
                        >
                          <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          <span className="truncate">"{quickKeyword.trim()}"</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Advertiser suggestions — Meta-style */}
                  {suggestions.advertisers.length > 0 && (
                    <div className="px-4 pt-3 pb-3 border-t border-slate-100">
                      <p className="text-[11px] font-bold text-slate-500 tracking-wide mb-2">Known advertisers</p>
                      <div className="space-y-1">
                        {suggestions.advertisers.map((a, i) => (
                          <button
                            key={`a-${i}`}
                            onClick={() => pickSuggestion(a.page_name, a.page_id || null)}
                            className="w-full text-left px-2 py-2 rounded-lg hover:bg-slate-50 flex items-center gap-3"
                            data-testid={`adv-suggest-${i}`}
                          >
                            <div className="w-9 h-9 rounded-full overflow-hidden bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center shrink-0">
                              {a.sample_image ? (
                                <img alt="" src={a.sample_image} referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-[12px] font-bold text-slate-500">{(a.page_name || '?').charAt(0).toUpperCase()}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-900 truncate">{a.page_name}</p>
                              <p className="text-[11px] text-slate-500 truncate flex items-center gap-1.5">
                                <PlatformIcon name="FACEBOOK" />
                                {a.ads_count} ad{a.ads_count === 1 ? '' : 's'} in library
                                {a.page_id && <> · PID {a.page_id.slice(0, 12)}…</>}
                              </p>
                            </div>
                            <ExternalLink className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty state */}
                  {suggestions.recent.length === 0 && suggestions.advertisers.length === 0 && !quickKeyword.trim() && (
                    <div className="px-4 py-6 text-center">
                      <p className="text-sm text-slate-500">Type a brand name to get started</p>
                      <p className="text-[11px] text-slate-400 mt-1">Known advertisers from past searches appear here</p>
                    </div>
                  )}

                  {/* Busy hint */}
                  {suggestBusy && (
                    <div className="px-4 py-1.5 text-[10px] text-slate-400 border-t border-slate-100 flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> updating…
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Search action */}
            <Button
              onClick={runQuickSearch}
              disabled={quickBusy || !quickKeyword.trim()}
              className="h-12 px-6 rounded-xl bg-[#2EB4AC] hover:bg-[#259993] text-white font-medium"
              data-testid="quick-search-btn"
            >
              {quickBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Search
            </Button>
          </div>

          {/* Clean minimal control row — only media & sort, just like Meta */}
          <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Sort: Most recent / Impressions h→l */}
              <div className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                <span className="font-semibold text-slate-600">Sort by</span>
              </div>
              <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 shadow-sm" role="tablist">
                {[
                  { v: 'recent', label: 'Most recent', icon: Clock },
                  { v: 'impressions', label: 'Impressions ↓', icon: TrendingUp },
                ].map(opt => {
                  const active = sortBy === opt.v;
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setSortBy(opt.v)}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-medium transition-all ${active ? 'bg-[#2EB4AC] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
                      data-testid={`sort-${opt.v}-btn`}
                    >
                      <Icon className="h-3 w-3" /> {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* How-it-works info button */}
              <button
                type="button"
                onClick={() => setShowHowItWorks(true)}
                className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100 transition-all"
                data-testid="how-it-works-btn"
                title="How Competitor Radar works"
              >
                <Info className="h-3 w-3" />
                How this works
              </button>
            </div>
          </div>

          {/* Helper text */}
          <p className="text-[11px] text-slate-400 mt-2">
            Showing <span className="font-semibold text-slate-600">active</span> ads only · India · <span className="font-semibold text-slate-600">all creatives</span> (images + videos) · {sortBy === 'recent' ? 'newest first' : 'highest impressions first'}
          </p>

          {/* Divider */}
          <div className="border-t border-slate-200 my-6" />

          {/* Saved Searches */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Bookmark className="h-4 w-4 text-slate-700 fill-slate-700" />
              <h3 className="text-sm font-semibold text-slate-900">Saved Searches</h3>
              {watchlist.length > 0 && (
                <Badge variant="outline" className="ml-1 text-[10px] h-5 border-slate-200 bg-slate-50 text-slate-600">{watchlist.length}</Badge>
              )}
            </div>

            {watchlist.length === 0 ? (
              <div className="text-center py-10 rounded-xl border border-dashed border-slate-200 bg-slate-50/40">
                <Bookmark className="h-6 w-6 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Type a brand above and hit Search — your saved searches will appear here.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {watchlist.map(item => (
                  <WatchlistRow
                    key={item.id}
                    item={item}
                    isPulling={pullingId === item.id}
                    onPull={pullNow}
                    onEdit={(it) => { setEditingItem(it); setFormOpen(true); }}
                    onDelete={deleteCompetitor}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Ads gallery */}
      {watchlist.length > 0 && (
        <Card className="p-5 border-slate-200">
          {/* Clean Meta-style results header */}
          <div className="pb-4 border-b border-slate-200 mb-4">
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[22px] font-bold text-slate-900 leading-tight">
                  ~{totalAds.toLocaleString()} {totalAds === 1 ? 'result' : 'results'}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Active ads matching your saved searches
                  {brandFilter !== 'all' && <> · <span className="font-semibold text-slate-700">{brandFilter}</span> <button onClick={() => setBrandFilter('all')} className="text-[#2EB4AC] hover:underline ml-1">(clear)</button></>}
                  {mediaFilter !== 'all' && <> · showing <span className="font-semibold text-slate-700 capitalize">{mediaFilter}s</span> only <button onClick={() => setMediaFilter('all')} className="text-[#2EB4AC] hover:underline ml-1">(clear)</button></>}
                  {activeTimeFilter !== 'any' && <> · active for <span className="font-semibold text-slate-700">{activeTimeLabel[activeTimeFilter]}</span> <button onClick={() => setActiveTimeFilter('any')} className="text-[#2EB4AC] hover:underline ml-1">(clear)</button></>}
                  {analyzedCount > 0 && <> · <span className="font-semibold text-violet-600">{analyzedCount}</span> analyzed</>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Segmented media type filter — All / Image / Video */}
                <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50/70 p-0.5" role="tablist" aria-label="Filter by media type" data-testid="media-filter-segmented">
                  {[
                    { key: 'all', label: 'All', count: ads.length, icon: null },
                    { key: 'image', label: 'Image', count: imageCount, icon: <ImageIcon className="h-3 w-3" /> },
                    { key: 'video', label: 'Video', count: videoCount, icon: <Video className="h-3 w-3" /> },
                  ].map(opt => {
                    const active = mediaFilter === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => setMediaFilter(opt.key)}
                        className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium transition-all ${
                          active
                            ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                        data-testid={`media-filter-${opt.key}`}
                      >
                        {opt.icon}
                        <span>{opt.label}</span>
                        <span className={`text-[10px] font-semibold tabular-nums ${active ? 'text-[#2EB4AC]' : 'text-slate-400'}`}>{opt.count}</span>
                      </button>
                    );
                  })}
                </div>
                {/* Active-time filter pill */}
                <Select value={activeTimeFilter} onValueChange={setActiveTimeFilter}>
                  <SelectTrigger className="h-9 w-auto min-w-[140px] rounded-full border-slate-200 bg-white text-xs font-medium text-slate-700" data-testid="active-time-filter-select">
                    <Clock className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                    <SelectValue placeholder="Any duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any duration</SelectItem>
                    <SelectItem value="lt7" data-testid="active-time-lt7">&lt; 7 days <span className="text-slate-400 ml-1">({activeTimeCounts.lt7})</span></SelectItem>
                    <SelectItem value="7to30" data-testid="active-time-7to30">7 – 30 days <span className="text-slate-400 ml-1">({activeTimeCounts['7to30']})</span></SelectItem>
                    <SelectItem value="30to90" data-testid="active-time-30to90">30 – 90 days <span className="text-slate-400 ml-1">({activeTimeCounts['30to90']})</span></SelectItem>
                    <SelectItem value="gt90" data-testid="active-time-gt90">90+ days <span className="text-slate-400 ml-1">({activeTimeCounts.gt90})</span></SelectItem>
                  </SelectContent>
                </Select>
                {brands.length > 1 && (
                  <Select value={brandFilter} onValueChange={setBrandFilter}>
                    <SelectTrigger className="h-9 w-auto min-w-[140px] rounded-full border-slate-200 bg-white text-xs font-medium text-slate-700" data-testid="brand-filter-select">
                      <Building2 className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                      <SelectValue placeholder="All brands" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All brands</SelectItem>
                      {brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Button size="sm" variant="outline" onClick={fetchAds} className="h-9 w-9 p-0 rounded-full border-slate-200" data-testid="refresh-ads-btn">
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading ads…
            </div>
          ) : ads.length === 0 ? (
            <div className="text-center py-12 bg-slate-50/50 rounded-xl border-2 border-dashed border-slate-200">
              <AlertCircle className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No ads yet — click the <Play className="inline h-3 w-3 mx-1" /> button on a saved search to pull now.</p>
            </div>
          ) : (
            <>
              {/* Selection toolbar */}
              {selectedIds.length > 0 ? (
                <div className="flex items-center justify-between gap-3 mb-4 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#2EB4AC]/10 to-cyan-50 border border-[#2EB4AC]/30" data-testid="selection-toolbar">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md bg-[#2EB4AC] text-white flex items-center justify-center">
                      <CheckSquare className="h-4 w-4" />
                    </div>
                    <p className="text-sm font-semibold text-slate-800">
                      {selectedIds.length} {selectedIds.length === 1 ? 'ad' : 'ads'} selected
                    </p>
                    <button onClick={selectAllVisible} className="text-xs text-[#2EB4AC] font-medium hover:underline ml-2" data-testid="select-all-btn">
                      {ads.every(a => selectedIds.includes(a.id)) ? 'Deselect all visible' : `Select all ${ads.length} visible`}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={clearSelection} className="h-8 text-xs" data-testid="clear-selection-btn">
                      <X className="h-3.5 w-3.5 mr-1" /> Clear
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setApplyBrandOpen(true)}
                      className="h-8 bg-[#2EB4AC] hover:bg-[#259993] text-white"
                      data-testid="apply-brand-btn"
                    >
                      <Tag className="h-3.5 w-3.5 mr-1.5" />
                      Apply brand
                    </Button>
                    <Button
                      size="sm"
                      onClick={bulkDelete}
                      disabled={bulkDeleting}
                      className="h-8 bg-rose-500 hover:bg-rose-600 text-white"
                      data-testid="bulk-delete-btn"
                    >
                      {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
                      Delete {selectedIds.length}
                    </Button>
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {displayedAds.map(ad => (
                  <AdCard
                    key={ad.id}
                    ad={ad}
                    analyzing={analyzingId === ad.id}
                    onAnalyze={analyzeAd}
                    onView={setSelectedAd}
                    onDelete={deleteAd}
                    selected={selectedIds.includes(ad.id)}
                    onToggleSelect={toggleSelect}
                    selectionMode={selectedIds.length > 0}
                  />
                ))}
              </div>
              {displayedAds.length === 0 && (mediaFilter !== 'all' || activeTimeFilter !== 'any') && (
                <div className="text-center py-10 bg-slate-50/50 rounded-xl border border-dashed border-slate-200 mt-2">
                  <p className="text-sm text-slate-500">No ads match your current filters. <button onClick={() => { setMediaFilter('all'); setActiveTimeFilter('any'); }} className="text-[#2EB4AC] font-medium hover:underline">Clear filters</button></p>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Form dialog (advanced) */}
      <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEditingItem(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingItem ? <><Edit3 className="h-4 w-4 text-[#2EB4AC]" /> Edit saved search</> : <><Plus className="h-4 w-4 text-[#2EB4AC]" /> Advanced search</>}
            </DialogTitle>
          </DialogHeader>
          <CompetitorForm
            initial={editingItem}
            onSave={saveCompetitor}
            onClose={() => { setFormOpen(false); setEditingItem(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Ad detail */}
      <AdDetailModal
        ad={selectedAd}
        open={!!selectedAd}
        onOpenChange={(o) => !o && setSelectedAd(null)}
        onAnalyze={analyzeAd}
        onSaveLearning={saveCompetitorLearning}
        onRephraseLearning={rephraseCompetitorLearning}
        analyzing={analyzingId === selectedAd?.id}
      />

      {/* Apply Brand dialog */}
      <Dialog open={applyBrandOpen} onOpenChange={(o) => { setApplyBrandOpen(o); if (!o) setApplyBrandValue(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-[#2EB4AC]" /> File {selectedIds.length} {selectedIds.length === 1 ? 'ad' : 'ads'} to a brand
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Brand name</Label>
              <Input
                list="competitor-brand-list"
                value={applyBrandValue}
                onChange={(e) => setApplyBrandValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applyBrandToSelected(); }}
                placeholder={competitorBrands.length ? "Start typing — past brands appear below" : "Type a brand name (e.g. Nutrabay, HealthKart)"}
                className="mt-1"
                autoFocus
                data-testid="apply-brand-input"
              />
              <datalist id="competitor-brand-list">
                {competitorBrands.map(b => <option key={b.name} value={b.name}>{b.name} · {b.count} {b.count === 1 ? 'ad' : 'ads'}</option>)}
              </datalist>
              <p className="text-[11px] text-slate-500 mt-1.5">
                {competitorBrands.length
                  ? `Suggestions are previously-tagged competitor brands (${competitorBrands.length}). Any new name also works — it will appear next time you apply.`
                  : 'Type any competitor brand name. It will be remembered for next time.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApplyBrandOpen(false)}>Cancel</Button>
            <Button
              onClick={applyBrandToSelected}
              disabled={!applyBrandValue.trim() || applyingBrand}
              className="bg-[#2EB4AC] hover:bg-[#259993] text-white"
              data-testid="apply-brand-confirm"
            >
              {applyingBrand ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              File to repository
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Futuristic AI extraction progress modal */}
      <ExtractionModal
        open={quickBusy || !!pullingId}
        keyword={quickKeyword || watchlist.find(w => w.id === pullingId)?.brand_name}
        mediaType={quickMedia}
        sortBy={sortBy}
        maxItems={quickMaxItems}
      />

      {/* How-it-works — futuristic explainer */}
      <HowItWorksModal open={showHowItWorks} onClose={() => setShowHowItWorks(false)} />
    </div>
  );
}

// ============================================================================
// HowItWorksModal — futuristic step-by-step explainer for the Radar module
// ============================================================================
const HowItWorksModal = ({ open, onClose }) => {
  if (!open) return null;
  const steps = [
    {
      Icon: Radar,
      title: 'Sweep',
      hue: 'from-emerald-500 to-teal-500',
      lead: 'You feed a keyword or brand',
      body: 'The radar pings Meta\u2019s live Ad Library across India, retrieving every currently-active creative in real time \u2014 images and videos included.',
    },
    {
      Icon: Layers,
      title: 'Harvest',
      hue: 'from-sky-500 to-blue-600',
      lead: 'Every asset is captured',
      body: 'We normalize each ad into a canonical shape \u2014 headline, body, page identity, media, first-seen date, impression range, landing URL \u2014 so nothing gets lost across platforms.',
    },
    {
      Icon: Database,
      title: 'Preserve',
      hue: 'from-violet-500 to-fuchsia-500',
      lead: 'Media is stored inside MongoDB',
      body: 'Instead of relying on Meta\u2019s CDN links that expire in days, images and videos are moved into durable GridFS storage. Ads survive every redeploy \u2014 forever.',
    },
    {
      Icon: Brain,
      title: 'Decode',
      hue: 'from-amber-500 to-orange-500',
      lead: 'AI extracts the pattern',
      body: 'On-demand, GPT-4o breaks each creative into its DNA \u2014 hook, promise, format, product callouts, sentiment, learning \u2014 and writes it back to the ad record.',
    },
    {
      Icon: Package,
      title: 'Archive',
      hue: 'from-rose-500 to-pink-500',
      lead: 'Everything ships to the Repository',
      body: 'One click sends the swept ads to your Competitor Repository, where they\u2019re filterable by brand, format, hook, keyword \u2014 becoming permanent creative intelligence.',
    },
    {
      Icon: Rocket,
      title: 'Learn & Ship',
      hue: 'from-indigo-500 to-purple-600',
      lead: 'The Learning Intelligence Infographic pulls signal',
      body: 'From the archive we generate a downloadable playbook: what\u2019s working across your category, what themes recur, and what to scale \u2014 packaged as a shareable Infographic.',
    },
  ];
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md" onClick={onClose}>
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl border border-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'radial-gradient(circle at 20% 0%, rgba(46, 180, 172, 0.18) 0%, transparent 45%), radial-gradient(circle at 85% 100%, rgba(167, 139, 250, 0.18) 0%, transparent 45%), linear-gradient(180deg, #050a14 0%, #08131f 100%)',
        }}
      >
        {/* Subtle grid overlay for that HUD-feel */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white flex items-center justify-center transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative z-10 overflow-y-auto max-h-[90vh]">
          {/* Header */}
          <div className="px-8 pt-8 pb-6 border-b border-white/5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2EB4AC]/15 border border-[#2EB4AC]/30 text-[#2EB4AC] text-[10px] font-bold tracking-[0.2em] uppercase mb-4">
              <Radar className="h-3 w-3" /> Competitor Radar · System View
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
              How your competitors <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#2EB4AC] via-cyan-300 to-violet-400">become intelligence</span>.
            </h2>
            <p className="text-sm text-slate-400 mt-3 max-w-2xl leading-relaxed">
              Six autonomous stages turn every live Meta ad in your category into a permanent, learnable, downloadable playbook.
            </p>
          </div>

          {/* Steps */}
          <div className="px-8 py-6 space-y-3">
            {steps.map((s, i) => {
              const Icon = s.Icon;
              return (
                <div key={s.title} className="group relative flex items-start gap-4 p-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors">
                  {/* Step number rail */}
                  <div className="flex flex-col items-center pt-1 flex-shrink-0">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.hue} text-white flex items-center justify-center shadow-lg shadow-black/40`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="text-[10px] font-bold tracking-[0.2em] text-slate-500 mt-2 uppercase">0{i + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <h3 className="text-white font-semibold text-lg tracking-tight">{s.title}</h3>
                      <span className="text-slate-400 text-sm">·</span>
                      <p className="text-[#2EB4AC] text-sm font-medium">{s.lead}</p>
                    </div>
                    <p className="text-slate-300 text-sm mt-1.5 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Callouts */}
          <div className="px-8 pb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { Icon: Shield, label: 'Ads stored durably in MongoDB GridFS', tint: 'text-emerald-300' },
              { Icon: Zap, label: 'Sort by Impressions or Most Recent', tint: 'text-cyan-300' },
              { Icon: Globe, label: 'Meta Ad Library · India · live active ads', tint: 'text-violet-300' },
            ].map((c) => {
              const Ico = c.Icon;
              return (
                <div key={c.label} className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                  <Ico className={`h-4 w-4 flex-shrink-0 ${c.tint}`} />
                  <p className="text-xs text-slate-300 leading-tight">{c.label}</p>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="px-8 pb-8 pt-2 flex items-center justify-between border-t border-white/5">
            <p className="text-[11px] text-slate-500 uppercase tracking-[0.18em]">Built with GPT-4o · Meta Ad Library · MongoDB GridFS</p>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#2EB4AC] to-teal-500 text-white text-sm font-semibold hover:shadow-lg hover:shadow-[#2EB4AC]/30 transition-all"
              data-testid="how-it-works-close"
            >
              Start sweeping
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

